/**
 * HamburgerButton — Animated hamburger/close toggle for GlobalHeader mobile.
 *
 * Uses Framer Motion staggered line transforms (Relume Navbar1 timing).
 * When `prefers-reduced-motion` is active, applies instant CSS transforms
 * instead of animations — the open/close state is still visually communicated.
 *
 * Duration and easing values sourced from @engagexp/legendary-css motion constants.
 * Framer Motion accepts seconds (tokens export millisecond strings — divide by 1000).
 */

import * as React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { duration, easing, stagger } from '@engagexp/legendary-css';
import styles from './global-header.module.css';

// ── Motion token values adapted for Framer Motion (seconds, not ms) ──────────
const D_BASE  = parseFloat(duration.base)  / 1000; // 0.25s
const D_FAST  = parseFloat(duration.fast)  / 1000; // 0.15s
const S_SLOW  = parseFloat(stagger.slow)   / 1000; // 0.10s — open stagger delay

// Parsed cubic-bezier arrays from easing tokens
// easing.smooth = "cubic-bezier(0.4, 0, 0.2, 1)"
const E_SMOOTH: [number, number, number, number] = [0.4, 0, 0.2, 1];
// easing.dramatic = "cubic-bezier(0.16, 1, 0.3, 1)" → emphasis / spring-like exit
void easing.dramatic; // token referenced for documentation; parsed inline above

const lineVariants = {
  closed: { rotate: 0, y: 0, opacity: 1, width: '1.375rem' },
  topOpen: { rotate: -45, y: 8 },
  bottomOpen: { rotate: 45, y: -8 },
  middleOpen: { opacity: 0, width: 0 },
};

interface HamburgerButtonProps {
  id: string;
  isOpen: boolean;
  panelId: string;
  onToggle: () => void;
}

/**
 * Forwarded ref allows GlobalHeader to return focus to the trigger button
 * when the mobile panel is dismissed (keyboard a11y, WCAG 2.1 AA).
 */
export const HamburgerButton = React.forwardRef<HTMLButtonElement, HamburgerButtonProps>(
  function HamburgerButton({ id, isOpen, panelId, onToggle }, ref) {
    const prefersReducedMotion = useReducedMotion();

    return (
      <button
        ref={ref}
        id={id}
        type="button"
        className={styles.hamburger}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        onClick={onToggle}
      >
        {prefersReducedMotion ? (
          /* Instant transforms — accessible open/close state without animation */
          <>
            <span
              className={styles.hamburgerLine}
              aria-hidden="true"
              style={isOpen ? { transform: 'translateY(7px) rotate(-45deg)' } : undefined}
            />
            <span
              className={styles.hamburgerLine}
              aria-hidden="true"
              style={isOpen ? { opacity: 0, width: 0 } : undefined}
            />
            <span
              className={styles.hamburgerLine}
              aria-hidden="true"
              style={isOpen ? { transform: 'translateY(-7px) rotate(45deg)' } : undefined}
            />
          </>
        ) : (
          /* Staggered Framer Motion transforms — delay matches Relume Navbar1 timing */
          /* Open: translate first (S_SLOW delay), then rotate (2×S_SLOW delay)       */
          /* Close: rotate → translate → middle re-appears (D_FAST × 2 delay)         */
          <>
            <motion.span
              className={styles.hamburgerLine}
              aria-hidden="true"
              animate={isOpen ? 'topOpen' : 'closed'}
              variants={lineVariants}
              transition={{ delay: isOpen ? S_SLOW : 0, duration: D_BASE, ease: E_SMOOTH }}
            />
            <motion.span
              className={styles.hamburgerLine}
              aria-hidden="true"
              animate={isOpen ? 'middleOpen' : 'closed'}
              variants={lineVariants}
              transition={{ delay: isOpen ? 0 : D_FAST * 2, duration: D_FAST, ease: E_SMOOTH }}
            />
            <motion.span
              className={styles.hamburgerLine}
              aria-hidden="true"
              animate={isOpen ? 'bottomOpen' : 'closed'}
              variants={lineVariants}
              transition={{ delay: isOpen ? S_SLOW : 0, duration: D_BASE, ease: E_SMOOTH }}
            />
          </>
        )}
      </button>
    );
  },
);

HamburgerButton.displayName = 'HamburgerButton';
